import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Group,
  Modal,
  Pagination,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { useState } from 'react'
import { useDisclosure } from '@mantine/hooks'
import {
  TbArrowRight,
  TbBuildingCommunity,
  TbCalendar,
  TbChevronRight,
  TbHome2,
  TbLayoutGrid,
  TbLink,
  TbList,
  TbMapPin,
  TbPlus,
  TbSearch,
  TbTestPipe,
  TbUser,
  TbX,
} from 'react-icons/tb'
import useSWR from 'swr'
import { API_URLS } from '../config/api'
import { RegionCascadeSelect } from '../components/RegionCascadeSelect'
import { fieldError, normalizePhone, validateVillageHead } from '../lib/villageHeadValidation'

export const Route = createFileRoute('/apps/$appId/villages/')({
  component: AppVillagesIndexPage,
})

interface APIVillage {
  id: string
  name: string
  isActive: boolean
  isDummy: boolean
  createdAt: string
  perbekel: string | null
  location: string | null
  codeVanguard: string | null
}

const statusConfig = {
  'active': { color: 'teal', label: 'Active' },
  'inactive': { color: 'orange', label: 'Inactive' },
}

const fetcher = (url: string) => fetch(url).then((res) => res.json())

function formatDate(dateStr: string) {
  if (!dateStr) return '-'
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  } catch (e) {
    return dateStr
  }
}

function VillageGridCard({ village, onClick }: { village: APIVillage; onClick: () => void }) {
  const status = village.isActive ? 'active' : 'inactive'
  const cfg = statusConfig[status as keyof typeof statusConfig]
  return (
    <Card
      withBorder
      radius="xl"
      padding="lg"
      className="village-card"
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <Group justify="space-between" mb="md">
        <ThemeIcon
          size={46}
          radius="md"
          variant="gradient"
          gradient={{ from: '#2563EB', to: '#7C3AED', deg: 135 }}
        >
          <TbHome2 size={22} />
        </ThemeIcon>
        <Group gap={6}>
          {village.isDummy && (
            <Badge color="yellow" variant="light" radius="sm" size="sm" leftSection={<TbTestPipe size={11} />}>
              Dummy
            </Badge>
          )}
          <Badge color={cfg.color} variant="light" radius="sm" size="sm">
            {cfg.label}
          </Badge>
        </Group>
      </Group>

      <Text fw={800} size="lg" mb={2}>
        {village.name}
      </Text>
      <Group gap={4} mb={4}>
        <TbMapPin size={13} color="var(--mantine-color-dimmed)" />
        <Text size="xs" c="dimmed">
          {village.location || 'No location details available'}
        </Text>
      </Group>

      <Group gap={4} mb={4}>
        <TbLink size={13} color="var(--mantine-color-dimmed)" />
        <Text size="xs" c="dimmed">{village.codeVanguard || '-'}</Text>
      </Group>

      <Divider my="sm" />

      <Stack gap={6}>
        <Group gap="xs">
          <TbUser size={13} color="var(--mantine-color-dimmed)" />
          <Text size="xs" c="dimmed">Village Head:</Text>
          <Text size="xs" fw={600}>{village.perbekel || '-'}</Text>
        </Group>
        <Group gap="xs">
          <TbCalendar size={13} color="var(--mantine-color-dimmed)" />
          <Text size="xs" c="dimmed">Created:</Text>
          <Text size="xs" fw={600}>{village.createdAt}</Text>
        </Group>
        {/* <Group gap="xs">
          <Avatar size={14} radius="xl" color="brand-blue" src={null} />
          <Text size="xs" c="dimmed">By:</Text>
          <Text size="xs" fw={600}>{village.createdBy}</Text>
        </Group> */}
      </Stack>

      <Button
        variant="light"
        color="brand-blue"
        size="compact-sm"
        fullWidth
        mt="md"
        radius="md"
        rightSection={<TbArrowRight size={14} />}
        styles={{ root: { fontSize: 12 } }}
      >
        View Details
      </Button>
    </Card>
  )
}

function VillageListRow({ village, onClick }: { village: APIVillage; onClick: () => void }) {
  const status = village.isActive ? 'active' : 'inactive'
  const cfg = statusConfig[status as keyof typeof statusConfig]
  return (
    <Paper
      withBorder
      radius="lg"
      p="md"
      className="village-list-row"
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <Group justify="space-between" wrap="nowrap">
        <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <ThemeIcon
            size={40}
            radius="md"
            variant="gradient"
            gradient={{ from: '#2563EB', to: '#7C3AED', deg: 135 }}
            style={{ flexShrink: 0 }}
          >
            <TbHome2 size={18} />
          </ThemeIcon>
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Group gap="sm" wrap="nowrap">
              <Text fw={700} size="sm" truncate>{village.name}</Text>
              {village.isDummy && (
                <Badge color="yellow" variant="light" radius="sm" size="xs" leftSection={<TbTestPipe size={10} />} style={{ flexShrink: 0 }}>
                  Dummy
                </Badge>
              )}
              <Badge color={cfg.color} variant="light" radius="sm" size="xs" style={{ flexShrink: 0 }}>
                {cfg.label}
              </Badge>
            </Group>
            <Group gap={6} wrap="nowrap">
              <TbMapPin size={12} color="var(--mantine-color-dimmed)" style={{ flexShrink: 0 }} />
              <Text size="xs" c="dimmed" truncate>
                {village.location || 'No location details available'}
              </Text>
            </Group>
          </Stack>
        </Group>

        <Group gap="xl" wrap="nowrap" visibleFrom="md" style={{ flexShrink: 0 }}>
          <Stack gap={0} align="center" w={100}>
            <Text size="xs" c="dimmed" fw={600} style={{ textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Village Head</Text>
            <Text size="xs" fw={600}>{village.perbekel || '-'}</Text>
          </Stack>
          <Stack gap={0} align="center" w={120}>
            <Text size="xs" c="dimmed" fw={600} style={{ textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Vanguard Code</Text>
            <Text size="xs" fw={600}>{village.codeVanguard || '-'}</Text>
          </Stack>
          <Stack gap={0} align="center" w={130}>
            <Text size="xs" c="dimmed" fw={600} style={{ textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Created</Text>
            <Text size="xs" fw={600}>{village.createdAt}</Text>
          </Stack>
          {/* <Stack gap={0} align="center">
            <Text size="xs" c="dimmed" fw={600} style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>Oleh</Text>
            <Group gap={4}>
              <Avatar size={16} radius="xl" color="brand-blue" src={null} />
              <Text size="xs" fw={600}>{village.createdBy}</Text>
            </Group>
          </Stack> */}
        </Group>

        <ActionIcon variant="light" color="brand-blue" radius="md" style={{ flexShrink: 0 }}>
          <TbChevronRight size={16} />
        </ActionIcon>
      </Group>
    </Paper>
  )
}

function AppVillagesIndexPage() {
  const { appId } = useParams({ from: '/apps/$appId' })
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] = useDisclosure(false)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  // Form State
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [form, setForm] = useState({
    name: '',
    desc: '',
    username: '',
    phone: '',
    nik: '',
    email: '',
    gender: '',
    idProvince: '',
    idRegency: '',
    idDistrict: ''
  })

  const isDesaPlus = appId === 'desa-plus'
  const apiUrl = isDesaPlus ? API_URLS.getVillages(page, searchQuery) : null

  const { data: response, error, isLoading, mutate } = useSWR(apiUrl, fetcher)
  const villages: APIVillage[] = response?.data || []

  const handleVillageClick = (villageId: string) => {
    navigate({ to: '/apps/$appId/villages/$villageId', params: { appId, villageId } })
  }

  const handleSearchChange = (val: string) => {
    setSearch(val)
    if (val.length >= 3 || val.length === 0) {
      setSearchQuery(val)
      setPage(1)
    }
  }

  const handleClearSearch = () => {
    setSearch('')
    setSearchQuery('')
    setPage(1)
  }

  const handleCreateVillage = async () => {
    // Village Data + region: wajib diisi.
    if (!form.name.trim() || !form.desc.trim() || !form.idProvince || !form.idRegency || !form.idDistrict) {
      notifications.show({
        title: 'Validation Error',
        message: 'Village name, description, and location are required.',
        color: 'red'
      })
      return
    }

    // Village Head Information: validasi format (username, NIK, email, phone, gender).
    const headError = validateVillageHead(form)
    if (headError) {
      notifications.show({
        title: 'Validation Error',
        message: headError,
        color: 'red'
      })
      return
    }

    setIsSubmitting(true)
    try {
      const res = await fetch(API_URLS.createVillages(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        // Simpan phone berawalan 62 agar konsisten dengan input dari mobile.
        body: JSON.stringify({ ...form, phone: normalizePhone(form.phone) })
      })

      if (res.ok) {
        await fetch(API_URLS.createLog(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'CREATE', message: `Desa baru didaftarkan: ${form.name}` })
        }).catch(console.error)

        notifications.show({
          title: 'Success',
          message: 'Village has been successfully registered.',
          color: 'teal'
        })
        mutate() // Refresh list
        closeCreateModal()
        setForm({
          name: '',
          desc: '',
          username: '',
          phone: '',
          nik: '',
          email: '',
          gender: '',
          idProvince: '',
          idRegency: '',
          idDistrict: ''
        })
      } else {
        notifications.show({
          title: 'Error',
          message: 'Failed to create village. Please try again.',
          color: 'red'
        })
      }
    } catch (e) {
      notifications.show({
        title: 'Network Error',
        message: 'Unable to reach API server.',
        color: 'red'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isDesaPlus) {
    return (
      <Container size="xl" py="xl">
        <Paper p="xl" radius="xl" className="glass" style={{ textAlign: 'center' }}>
          <TbBuildingCommunity size={48} color="gray" opacity={0.5} />
          <Title order={3} mt="md">General Management</Title>
          <Text c="dimmed">This feature is currently customized for Desa+. Other apps coming soon.</Text>
        </Paper>
      </Container>
    )
  }

  return (
    <Stack gap="xl">
      <Modal 
        opened={createModalOpened} 
        onClose={closeCreateModal} 
        title={<Text fw={700} size="lg">Register New Village</Text>}
        radius="xl"
        size="lg"
      >
        <Stack gap="md">
          <Box>
            <Text size="xs" fw={700} c="dimmed" mb={8} style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Village Data
            </Text>
            <Stack gap="sm">
              <TextInput
                label="Village Name"
                placeholder="e.g. Darmasaba"
                required
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.currentTarget.value }))}
              />
              <RegionCascadeSelect
                required
                value={{ idProvince: form.idProvince, idRegency: form.idRegency, idDistrict: form.idDistrict }}
                onChange={(region) => setForm(prev => ({ ...prev, ...region }))}
              />
              <Textarea
                label="Description"
                placeholder="Short description about the village..."
                minRows={3}
                required
                value={form.desc}
                onChange={(e) => setForm(prev => ({ ...prev, desc: e.currentTarget.value }))}
              />
            </Stack>
          </Box>

          <Divider label="Village Head Information" labelPosition="center" my="sm" />

          <Box>
            <SimpleGrid cols={2} spacing="md">
              <TextInput
                label="Head Name (Username)"
                placeholder="Full name of village head"
                required
                value={form.username}
                onChange={(e) => setForm(prev => ({ ...prev, username: e.currentTarget.value }))}
                error={fieldError('username', form.username)}
              />
              <TextInput
                label="NIK"
                placeholder="16-digit identity number"
                required
                value={form.nik}
                onChange={(e) => setForm(prev => ({ ...prev, nik: e.currentTarget.value }))}
                error={fieldError('nik', form.nik)}
              />
            </SimpleGrid>

            <SimpleGrid cols={2} spacing="md" mt="sm">
              <TextInput
                label="Email"
                placeholder="Email address"
                required
                value={form.email}
                onChange={(e) => setForm(prev => ({ ...prev, email: e.currentTarget.value }))}
                error={fieldError('email', form.email)}
              />
              <TextInput
                label="Phone"
                placeholder="8XX-XXX-XXX"
                required
                leftSection={<Text size="sm" c="dimmed">+62</Text>}
                leftSectionWidth={44}
                value={form.phone}
                onChange={(e) => setForm(prev => ({ ...prev, phone: e.currentTarget.value }))}
                error={fieldError('phone', form.phone)}
              />
            </SimpleGrid>

            <Select 
              label="Gender" 
              placeholder="Select gender"
              data={[{ label: 'Male', value: 'M' }, { label: 'Female', value: 'F' }]}
              mt="sm"
              required
              value={form.gender}
              onChange={(val) => setForm(prev => ({ ...prev, gender: val || '' }))}
            />
          </Box>

          <Button 
            fullWidth 
            mt="lg" 
            radius="md" 
            size="md"
            variant="gradient"
            gradient={{ from: '#2563EB', to: '#7C3AED', deg: 135 }}
            loading={isSubmitting}
            onClick={handleCreateVillage}
          >
            Create Village
          </Button>
        </Stack>
      </Modal>

      <Group justify="space-between" align="flex-end">
        <Stack gap={4}>
          <Title order={3}>Village List</Title>
          <Text size="sm" c="dimmed">
            {isLoading ? 'Loading data...' : `${response?.totalData || 0} villages registered in the Desa+ platform`}
          </Text>
        </Stack>
        <Button
          variant="gradient"
          gradient={{ from: '#2563EB', to: '#7C3AED', deg: 135 }}
          leftSection={<TbPlus size={18} />}
          radius="md"
          onClick={openCreateModal}
        >
          Create New Village
        </Button>
      </Group>

      <Group justify="space-between">
        <TextInput
          placeholder="Search village, district, city..."
          leftSection={<TbSearch size={16} />}
          rightSection={
            search ? (
              <ActionIcon variant="transparent" color="gray" onClick={handleClearSearch} size="sm">
                <TbX size={14} />
              </ActionIcon>
            ) : null
          }
          value={search}
          onChange={(e) => handleSearchChange(e.currentTarget.value)}
          radius="md"
          style={{ flex: 1, maxWidth: 400 }}
        />
        <SegmentedControl
          value={viewMode}
          onChange={(v) => setViewMode(v as 'grid' | 'list')}
          data={[
            { value: 'grid', label: <Tooltip label="Grid View"><Box><TbLayoutGrid size={16} /></Box></Tooltip> },
            { value: 'list', label: <Tooltip label="List View"><Box><TbList size={16} /></Box></Tooltip> },
          ]}
          radius="md"
        />
      </Group>

      {isLoading ? (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          {[1, 2, 3].map((i) => (
            <Card key={i} withBorder radius="xl" padding="lg" style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Text c="dimmed">Loading...</Text>
            </Card>
          ))}
        </SimpleGrid>
      ) : error ? (
        <Paper p="xl" radius="xl" className="glass" style={{ textAlign: 'center' }}>
          <TbBuildingCommunity size={40} color="red" opacity={0.4} />
          <Text c="red" mt="md">Failed to load data from API.</Text>
        </Paper>
      ) : villages.length === 0 ? (
        <Paper p="xl" radius="xl" className="glass" style={{ textAlign: 'center' }}>
          <TbBuildingCommunity size={40} color="gray" opacity={0.4} />
          <Text c="dimmed" mt="md">No villages match your search.</Text>
        </Paper>
      ) : viewMode === 'grid' ? (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          {villages.map((village) => (
            <VillageGridCard
              key={village.id}
              village={village}
              onClick={() => handleVillageClick(village.id)}
            />
          ))}
        </SimpleGrid>
      ) : (
        <Stack gap="sm">
          {villages.map((village) => (
            <VillageListRow
              key={village.id}
              village={village}
              onClick={() => handleVillageClick(village.id)}
            />
          ))}
        </Stack>
      )}

      {!isLoading && !error && response?.totalPage > 0 && (
        <Group justify="center" mt="xl">
          <Pagination
            value={page}
            onChange={setPage}
            total={response.totalPage}
            radius="md"
            withEdges={false}
          />
        </Group>
      )}
    </Stack>
  )
}
